import { PrintRows, PrintRunning, type Rec, SignatureMark, useCompany } from '@tnn/app-kit'
import { bahtText, formatDate, formatMoney, formatPhone, formatTaxId, type RefMap } from '@tnn/core'
import type { CustomerReceiptLine } from '@tnn/domain'
import { useTranslation } from 'react-i18next'
import classes from './document.module.css'
import { CustomerAddress, CustomerTaxLine, LinesClose } from './InvoiceDocument'

/**
 * A4 receipt in the customer's name (ใบเสร็จรับเงิน) in place of a counter receipt: who paid, the freight of each bill,
 * what they withheld, the money received in words, the day it came and the receipt it replaces, and under ผู้รับเงิน
 * whoever took the money that day (`receivedBy`, no ink). Freight is exempt from VAT: no VAT line, never a tax
 * invoice. Same page layout as the invoice.
 */
export function CustomerReceiptDocument({ record, refs, copy = false }: { record: Rec; refs: RefMap; copy?: boolean }) {
  const { t } = useTranslation()
  const company = useCompany().data
  const lines = (record.lines ?? []) as CustomerReceiptLine[]
  const received = Number(record.received ?? 0)
  const withheld = Number(record.whtAmount ?? 0)
  const branch = (b?: string | null) =>
    !b || b === '00000' ? t('accounting:customer.headOffice') : `${t('accounting:customer.fields.branch')} ${b}`
  const p = (k: string, o?: Record<string, unknown>) => t(`accounting:customerReceipt.print.${k}`, o)
  const f = (k: string) => t(`accounting:customerReceipt.fields.${k}`)
  const replaced = refs['tms.receipt']?.[String(record.receiptId)] ?? ''
  const docName = t('accounting:customerReceipt.documentTitle')
  const issued = record.issueDate ? formatDate(String(record.issueDate), { style: 'long' }) : ''
  const copyLabel = t(copy ? 'accounting:invoice.print.copy' : 'accounting:invoice.print.original')

  return (
    <article className={classes.doc}>
      <PrintRunning start={[company?.name, docName]} end={[String(record.number ?? ''), issued, copyLabel]} />
      <header className={classes.head}>
        <div className={classes.from}>
          <div className={classes.company}>{company?.name}</div>
          <div>{company?.address}</div>
          <div>
            {company?.taxId && `${t('accounting:customer.fields.taxId')} ${formatTaxId(company.taxId)} · ${branch(company.branch)}`}
          </div>
          <div>{[formatPhone(company?.phone), company?.email].filter(Boolean).join(' · ')}</div>
        </div>
        <div className={classes.title}>
          <div className={classes.docName}>{docName}</div>
          <div className={classes.copy}>{copyLabel}</div>
        </div>
      </header>

      <section className={classes.parties}>
        <div>
          <div className={classes.caption}>{p('receivedFrom')}</div>
          <div className={classes.strong}>{String(record.name ?? '')}</div>
          <CustomerAddress customer={record} />
          <CustomerTaxLine customer={record} />
        </div>
        <dl className={classes.meta}>
          <dt>{f('number')}</dt>
          <dd className={classes.strong}>{String(record.number ?? '')}</dd>
          <dt>{t('accounting:invoice.fields.issueDate')}</dt>
          <dd>{issued}</dd>
          <dt>{f('paidOn')}</dt>
          <dd>{formatDate(String(record.paidOn), { style: 'long' })}</dd>
        </dl>
      </section>

      <table className={classes.lines}>
        <thead>
          <tr>
            <th style={{ width: '6%' }}>#</th>
            <th>{t('accounting:invoice.line.description')}</th>
            <th data-num style={{ width: '20%' }}>
              {t('accounting:invoice.line.amount')}
            </th>
          </tr>
        </thead>
        <PrintRows
          rows={lines.map((line, i) => (
            <tr key={`${line.waybillId}:${line.side}`}>
              <td>{i + 1}</td>
              <td>
                {p('line', { number: line.waybillNumber ?? '' })} ({t(`accounting:customerReceipt.side.${line.side}`)})
              </td>
              <td data-num>{formatMoney(line.amount)}</td>
            </tr>
          ))}
          close={
            <LinesClose columns={3}>
              <section className={classes.summary}>
                <div className={classes.words}>
                  <div className={classes.caption}>{t('accounting:invoice.totals.inWords')}</div>
                  <div className={classes.strong}>({bahtText(received)})</div>
                  <p className={classes.notes}>
                    {p('replaces', { number: replaced, date: formatDate(String(record.paidOn), { style: 'long' }) })}
                  </p>
                  {record.notes ? <p className={classes.notes}>{String(record.notes)}</p> : null}
                </div>
                <dl className={classes.totals}>
                  <dt>{f('amount')}</dt>
                  <dd>{formatMoney(Number(record.amount ?? 0))}</dd>
                  {withheld > 0 && (
                    <>
                      <dt>{f('whtAmount')}</dt>
                      <dd>-{formatMoney(withheld)}</dd>
                    </>
                  )}
                  <dt className={classes.grand}>{f('received')}</dt>
                  <dd className={classes.grand}>{formatMoney(received)}</dd>
                </dl>
              </section>

              <footer className={classes.signs}>
                <SignatureMark
                  label={p('receiver')}
                  named={record.receivedBy ? { name: String(record.receivedBy), at: String(record.paidOn) } : null}
                />
              </footer>
            </LinesClose>
          }
        />
      </table>
    </article>
  )
}
